import api from './api';

class InspectionService {
  /**
   * Create a new vehicle inspection
   */
  async createInspection(inspectionData) {
    try {
      const response = await api.post('/inspections', {
        vehicleId: inspectionData.vehicleId,
        inspectorId: inspectionData.inspectorId,
        driverId: inspectionData.driverId,
        companyId: inspectionData.companyId,
        type: inspectionData.inspectionType,
        inspectionNumber: this.generateInspectionNumber(),
        scheduledDate: new Date().toISOString(),
        mileage: inspectionData.mileage || 0,
        engineHours: inspectionData.engineHours || 0,
        priority: 'medium',
        notes: `${inspectionData.inspectionType} inspection started`,
      });

      return response.inspection;
    } catch (error) {
      console.error('Error creating inspection:', error);
      throw new Error(error.message || 'Failed to create inspection');
    }
  }

  /**
   * Upload photo to inspection
   */
  async uploadPhoto(inspectionId, photoData) {
    try {
      let photoPayload = photoData.photo || photoData.base64 || null;

      // Use preview only if it is already a base64 data URL
      if (!photoPayload && typeof photoData.preview === 'string' && photoData.preview.startsWith('data:image/')) {
        photoPayload = photoData.preview;
      }

      if (!photoPayload && photoData.file) {
        photoPayload = await this.fileToBase64(photoData.file);
      }

      if (!photoPayload) {
        throw new Error('Unable to read photo data for upload');
      }

      const filename = photoData.filename || photoData.name || photoData.file?.name || `inspection_photo_${Date.now()}.jpg`;

      const response = await api.post(`/inspections/${inspectionId}/photos`, {
        photo: photoPayload,
        category: photoData.category,
        description: photoData.notes || photoData.description || `${photoData.category || 'inspection'} photo`,
        defectId: photoData.defectId || null,
        filename,
      });

      return response.photo;
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw new Error(error.message || 'Failed to upload photo');
    }
  }

  /**
   * Add defect to inspection
   */
  async addDefect(inspectionId, defectData) {
    try {
      const response = await api.post(`/inspections/${inspectionId}/defects`, {
        category: defectData.category,
        title: defectData.title,
        description: defectData.description,
        severity: defectData.severity || 'minor',
        location: defectData.location,
        repairNotes: defectData.repairNotes || '',
        notes: defectData.notes || '',
        status: defectData.status || 'reported',
        estimatedCost: defectData.estimatedCost || 0,
        priority: defectData.priority || 'medium',
        photos: (defectData.photos || []).map(photo => ({
          id: photo.id,
          url: photo.url || photo.preview,
          thumbnailUrl: photo.thumbnailUrl || photo.preview,
          category: photo.category,
          notes: photo.notes || ''
        })),
        reportedAt: defectData.reportedAt || new Date().toISOString(),
        workOrder: defectData.workOrder || ''
      });

      return response.defect;
    } catch (error) {
      console.error('Error adding defect:', error);
      throw new Error(error.message || 'Failed to add defect');
    }
  }

  /**
   * Complete inspection with digital signature
   */
  async completeInspection(inspectionId, completionData) {
    try {
      const requestData = {
        digitalSignature: completionData.signature?.signatureDataUrl || completionData.signature,
        signedBy: completionData.signedBy,
        checklist: completionData.checklist,
        defects: completionData.defects || [], // Include defects in completion
        passed: completionData.passed,
        completionNotes: completionData.notes || '',
        location: {
          address: completionData.location || 'Current location',
        },
        mileage: completionData.mileage || 0,
        engineHours: completionData.engineHours || 0,
      };

      // Only include coordinates if they are provided and valid
      if (completionData.coordinates && completionData.coordinates.trim()) {
        requestData.location.coordinates = completionData.coordinates;
      }

      const response = await api.put(`/inspections/${inspectionId}/complete`, requestData);

      return response.inspection;
    } catch (error) {
      console.error('Error completing inspection:', error);
      throw new Error(error.message || 'Failed to complete inspection');
    }
  }

  /**
   * Get inspection details
   */
  async getInspection(inspectionId) {
    try {
      const response = await api.get(`/inspections/${inspectionId}`);
      return response.inspection;
    } catch (error) {
      console.error('Error getting inspection:', error);
      throw new Error(error.message || 'Failed to get inspection');
    }
  }

  /**
   * Get inspection history for a vehicle
   */
  async getVehicleInspections(vehicleId, limit = 20) {
    try {
      const response = await api.get(`/inspections/vehicles/${vehicleId}/inspections?limit=${limit}`);
      return response.inspections || [];
    } catch (error) {
      console.error('Error getting vehicle inspections:', error);
      throw new Error(error.message || 'Failed to get vehicle inspections');
    }
  }

  /**
   * Update inspection
   */
  async updateInspection(inspectionId, updateData) {
    try {
      const response = await api.put(`/inspections/${inspectionId}`, updateData);
      return response.inspection;
    } catch (error) {
      console.error('Error updating inspection:', error);
      throw new Error(error.message || 'Failed to update inspection');
    }
  }

  /**
   * Delete inspection
   */
  async deleteInspection(inspectionId) {
    try {
      await api.delete(`/inspections/${inspectionId}`);
      return true;
    } catch (error) {
      console.error('Error deleting inspection:', error);
      throw new Error(error.message || 'Failed to delete inspection');
    }
  }

  /**
   * Generate a unique inspection number
   */
  generateInspectionNumber() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const time = String(now.getTime()).slice(-6); // Last 6 digits of timestamp
    
    return `INS-${year}${month}${day}-${time}`;
  }

  /**
   * Convert image file to base64 data URL
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Get current GPS coordinates (if available)
   */
  async getCurrentLocation() {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve(`${position.coords.latitude},${position.coords.longitude}`);
        },
        () => {
          resolve(null); // Resolve with null if location access denied
        },
        { timeout: 5000 }
      );
    });
  }
}

export const inspectionService = new InspectionService();
export default inspectionService;
